import {tx, ptx, usePageText} from '../lib/page-text';
import { useState, type ReactNode } from 'react';
import { useT } from '../lib/i18n';
import { CopyIcon, CheckIcon, HelpIcon, InfoIcon, WarnIcon, ErrorIcon, CloseIcon } from './icons';

export function Code({ value, large = false }: { value: string; large?: boolean }) {
  usePageText();
  const [copied, setCopied] = useState(false);
  return (
    <div className={`code ${large ? 'lg' : ''}`}>
      <span className="val">{value}</span>
      <button
        className="copy"
        onClick={async () => {
          try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
        }}
      >
        {copied ? <CheckIcon /> : <CopyIcon />} {copied ? tx("copied") : tx("copy")}
      </button>
    </div>
  );
}

export function CodeBlock({ value }: { value: string }) {
  usePageText();
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <pre className="code lg" style={{ margin: 0 }}>{value}</pre>
      <button
        className="copy"
        style={{ position: 'absolute', top: 10, right: 10 }}
        onClick={async () => {
          try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
        }}
      >
        {copied ? <CheckIcon /> : <CopyIcon />} {copied ? tx("copied") : tx("copy")}
      </button>
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  usePageText();
  const { t } = useT();
  if (status === 'LIVE') return <span className="badge live"><span className="pulse"/>{t('status.live')}</span>;
  if (status === 'IDLE') return <span className="badge idle">{t('status.idle')}</span>;
  if (status === 'ENDED') return <span className="badge">{t('status.ended')}</span>;
  if (status === 'ARCHIVED') return <span className="badge">{t('status.archived')}</span>;
  return <span className="badge">{status}</span>;
}

export function Toggle({ on, onChange, label, desc }: { on: boolean; onChange: (v: boolean) => void; label: string; desc: string }) {
  usePageText();
  return (
    <div className="toggle-row">
      <div className="label-block">
        <span className="name">{label}</span>
        <span className="desc">{desc}</span>
      </div>
      <button type="button" className={`toggle ${on ? 'on' : ''}`} onClick={() => onChange(!on)} aria-pressed={on}/>
    </div>
  );
}

export function Modal({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  usePageText();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, body, action }: { icon: ReactNode; title: string; body: string; action?: ReactNode }) {
  usePageText();
  return (
    <div className="empty">
      <div className="icon-big">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

/**
 * Guidance primitives.
 *
 * The console is dense and most controls are jargon ("scene", "lower third",
 * "WHIP"). Rather than a tour or a modal, every unclear control gets its
 * explanation inline, right where the control is:
 *
 *   <HelpTip>      a (?) affordance; text on hover AND keyboard focus
 *   <Help>         one-line muted explanation under a heading or control
 *   <StepHeading>  numbered card heading, so a multi-step panel reads in order
 *   <Alert>        errors/warnings that need to be read, not a 11px badge
 *   <BlockedHint>  why the primary action is currently disabled
 */

export function HelpTip({ text }: { text: string }) {
  usePageText();
  return (
    <span className="helptip" tabIndex={0} role="note" aria-label={text}>
      <HelpIcon />
      <span className="helptip-bubble">{text}</span>
    </span>
  );
}

export function Help({ children }: { children: ReactNode }) {
  usePageText();
  return <p className="help">{children}</p>;
}

export function StepHeading({ step, title, help, done }: { step: number; title: string; help?: string; done?: boolean }) {
  usePageText();
  return (
    <>
      <h3 className="step-heading">
        <span className={`step-num ${done ? 'done' : ''}`}>{done ? <CheckIcon /> : step}</span>
        {title}
        {help && <HelpTip text={help} />}
      </h3>
    </>
  );
}

export function Alert({
  kind = 'error', title, children, onDismiss,
}: { kind?: 'error' | 'warn' | 'info'; title: string; children?: ReactNode; onDismiss?: () => void }) {
  usePageText();
  const Icon = kind === 'error' ? ErrorIcon : kind === 'warn' ? WarnIcon : InfoIcon;
  return (
    <div className={`alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span className="alert-icon"><Icon /></span>
      <div className="alert-body">
        <strong>{title}</strong>
        {children && <div className="alert-text">{children}</div>}
      </div>
      {onDismiss && (
        <button className="alert-close" onClick={onDismiss} aria-label={tx("Dismiss")}><CloseIcon /></button>
      )}
    </div>
  );
}

export function BlockedHint({ reasons }: { reasons: string[] }) {
  usePageText();
  if (reasons.length === 0) return null;
  return (
    <div className="blocked-hint">
      <WarnIcon size={12} />
      <span>{reasons.length === 1 ? reasons[0] : ptx("{v0} things left: {v1}", {v0: (reasons.length), v1: (reasons.join(' · '))})}</span>
    </div>
  );
}
